<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import { SurveyResponseCreateInput } from '@/__generated__/gateway/graphql';
import SurveyModal from '@/components/Survey/SurveyModal.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import { useCrafterSurvey } from '@/shared/composables/crafters/useCrafterSurvey';
import { useTranslations } from '@/shared/composables/useTranslations';
import { getLanguageFromContext } from '@/shared/utils/gateway/context';

export type SurveyDisplayMode = 'inline' | 'modal';
export type SurveyButtonTheme =
  | 'primary'
  | 'primary-light'
  | 'primary-transparent'
  | 'white'
  | 'gray'
  | 'gray-dark'
  | 'danger';

const props = withDefaults(
  defineProps<{
    /** The subject of the survey (e.g., 'pattern', 'product', 'order') */
    subject: string;
    /** The action being performed (e.g., 'show', 'purchase', 'view') */
    action: string;
    /** Optional subject ID for specific entity surveys */
    subjectId?: string;
    /** Display mode: inline content or modal popup */
    mode?: SurveyDisplayMode;
    /** Title to display (falls back to translation if not provided) */
    title?: string;
    /** Prompt text to display (falls back to translation if not provided) */
    prompt?: string;
    /** Button theme when in modal mode */
    buttonTheme?: SurveyButtonTheme;
    /** Enable debug logging */
    debug?: boolean;
    /** Custom CSS classes for the container */
    containerClasses?: string;
    /** Custom trigger HTML content */
    customTriggerHtml?: string;
  }>(),
  {
    subjectId: undefined,
    mode: 'inline',
    title: undefined,
    prompt: undefined,
    buttonTheme: 'primary-transparent',
    debug: false,
    containerClasses: 'mt-6 flex flex-col gap-1.5',
    customTriggerHtml: undefined
  }
);

const emit = defineEmits<{
  /** Emitted when survey is submitted successfully */
  (e: 'submit'): void;
  /** Emitted when survey availability changes */
  (e: 'availability-change', available: boolean): void;
}>();

defineSlots<{
  /** Custom trigger content for modal mode */
  trigger?: () => unknown;
  /** Custom title content */
  title?: () => unknown;
  /** Custom prompt content */
  prompt?: () => unknown;
}>();

const { t } = useTranslations();

const showSurveyModal = ref(false);

// Create survey config based on props
const surveyConfig = computed<SurveyResponseCreateInput>(() => ({
  action: props.action,
  subject: props.subject,
  subject_id: props.subjectId,
  locale: getLanguageFromContext()
}));

// Check if survey is available - start with false and trigger after mount
const surveyEnabled = ref(false);
const { createdSurvey } = useCrafterSurvey(surveyConfig, surveyEnabled);

// Trigger survey check after component mounts
onMounted(() => {
  surveyEnabled.value = true;
});

// Only show the survey prompt if a survey is available
const isSurveyAvailable = computed(() => {
  return !!createdSurvey.value;
});

// Watch for survey availability changes and emit event
watch(
  isSurveyAvailable,
  (available) => {
    emit('availability-change', available);

    // Control visibility of the mount element to prevent flashing
    const mountElement = document.getElementById('js-survey');
    if (mountElement) {
      if (available) {
        mountElement.classList.remove('hidden');
      } else {
        mountElement.classList.add('hidden');
      }
    }
  },
  { immediate: true }
);

const openSurvey = () => {
  showSurveyModal.value = true;
};

const onSurveySubmit = () => {
  emit('submit');
  if (props.mode === 'modal') {
    showSurveyModal.value = false;
  }
};

// Default content for title and prompt
const defaultTitle = computed(
  () => props.title || t('product.survey.title', 'Survey')
);

const defaultPrompt = computed(
  () =>
    props.prompt ||
    t(
      'product.survey.prompt',
      'Help us improve by answering a few questions about your experience'
    )
);
</script>

<template>
  <div v-if="isSurveyAvailable">
    <!-- Inline mode -->
    <div v-if="mode === 'inline'" :class="containerClasses">
      <h3>
        <slot name="title">{{ defaultTitle }}</slot>
      </h3>
      <div class="text-sm text-gray-600">
        <slot name="prompt">{{ defaultPrompt }}</slot>
      </div>
      <SurveyModal
        v-model:active="showSurveyModal"
        :input="surveyConfig"
        :title="defaultTitle"
        @submit="onSurveySubmit"
      />
      <ButtonText
        :theme="buttonTheme"
        class="mt-2 !justify-start !px-0"
        @click="openSurvey"
      >
        {{ t('product.survey.open', 'Open Survey') }}
      </ButtonText>
    </div>

    <!-- Modal mode -->
    <div v-else-if="mode === 'modal'">
      <slot name="trigger">
        <!-- Custom HTML trigger if provided -->
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="customTriggerHtml"
          @click="openSurvey"
          v-html="customTriggerHtml"
        ></div>
        <!-- eslint-enable vue/no-v-html -->
        <!-- Default button trigger -->
        <ButtonText
          v-else
          :theme="buttonTheme"
          class="text-left text-sm"
          @click="openSurvey"
        >
          <slot name="prompt">{{ defaultPrompt }}</slot>
        </ButtonText>
      </slot>

      <SurveyModal
        v-model:active="showSurveyModal"
        :input="surveyConfig"
        :title="defaultTitle"
        @submit="onSurveySubmit"
      />
    </div>
  </div>
</template>
